import React, { useEffect, useState } from "react";
import {
	Autocomplete,
	Avatar,
	Box,
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	MenuItem,
	TextField,
	Typography,
} from "@mui/material";
import { getSalesMenu } from "../../../../../../../services/Admin/SalesScreenApi";
import { getImageUrl } from "../../../../../../../services/indexMediaBaseUrl";

const getVisibleProducts = (products) =>
	Array.isArray(products)
		? products.filter((product) => Number(product?.status) !== 0)
		: [];

const AddItemDialog = ({ open, onClose, onAdd }) => {
	const [loadingMenu, setLoadingMenu] = useState(false);
	const [categories, setCategories] = useState([]);
	const [selectedCategory, setSelectedCategory] = useState("");

	const [formData, setFormData] = useState({
		product_id: "",
		quantity: 1,
		price: "",
		note: "",
		status: "pending",
	});

	// جلب قائمة الفئات والمنتجات
	useEffect(() => {
		if (!open) return;

		const fetchMenu = async () => {
			setLoadingMenu(true);
			try {
				const res = await getSalesMenu();
				if (Array.isArray(res?.data)) {
					setCategories(res.data);
				}
			} catch (err) {
				console.error("فشل تحميل القائمة:", err);
			} finally {
				setLoadingMenu(false);
			}
		};

		fetchMenu();
	}, [open]);

	// الحصول على المنتجات المفلترة حسب الفئة المختارة
	const getFilteredProducts = () => {
		if (!selectedCategory) {
			const allProducts = [];
			categories.forEach((category) => {
				getVisibleProducts(category.products).forEach((product) => {
						allProducts.push({
							...product,
							categoryName: category.name,
						});
					});
			});
			return allProducts;
		}

		const category = categories.find((cat) => cat.id === selectedCategory);
		return getVisibleProducts(category?.products);
	};

	const handleFieldChange = (e) => {
		const { name, value } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: value,
		}));
	};

	const handleProductSelect = (productId) => {
		const filteredProducts = getFilteredProducts();
		const selectedProduct = filteredProducts.find((p) => p.id === Number(productId));

		setFormData((prev) => ({
			...prev,
			product_id: productId,
			price: selectedProduct?.price || "",
		}));
	};

	const handleCategoryChange = (categoryId) => {
		setSelectedCategory(categoryId);
		setFormData((prev) => ({
			...prev,
			product_id: "",
			price: "",
		}));
	};

	const handleAddItem = () => {
		// التحقق من الحقول المطلوبة
		if (!formData.product_id || !formData.quantity || formData.price === "") {
			alert("يرجى ملء جميع الحقول المطلوبة");
			return;
		}

		onAdd({
			product_id: formData.product_id,
			drink_id: "",
			quantity: Number(formData.quantity),
			price: formData.price,
			note: formData.note,
			status: "pending",
		});

		// إعادة تعيين النموذج فوراً بعد الإضافة
		resetForm();
	};

	const resetForm = () => {
		setFormData({
			product_id: "",
			quantity: 1,
			price: "",
			note: "",
			status: "pending",
		});
		setSelectedCategory("");
	};

	const handleClose = () => {
		// إعادة تعيين النموذج عند غلق الديالوج
		resetForm();
		onClose();
	};

	const filteredProducts = getFilteredProducts();

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="md"
			fullWidth
			dir="rtl"
			PaperProps={{
				sx: {
					width: { xs: "100%", md: 860 },
					maxWidth: "100%",
				},
			}}
		>
			<DialogTitle sx={{ fontWeight: "bold", textAlign: "right" }}>
				إضافة منتج
			</DialogTitle>

			<DialogContent sx={{ pt: 2, px: { xs: 2, md: 3 }, width: "100%" }}>
				{/* الفئة والمنتج */}
				<Grid container spacing={2}>
					<Grid size={12}>
						<TextField
							select
							label="الفئة"
							sx={{ width: "100%" }}
							value={selectedCategory || ""}
							onChange={(e) => handleCategoryChange(e.target.value)}
							fullWidth
							disabled={loadingMenu}
							SelectProps={{
								displayEmpty: true,
								MenuProps: {
									PaperProps: {
										sx: {
											minWidth: 340,
											width: 420,
											maxWidth: "min(90vw, 520px)",
											direction: "rtl",
											"& .MuiMenuItem-root": {
												whiteSpace: "nowrap",
												overflow: "hidden",
												textOverflow: "ellipsis",
											},
										},
									},
								},
							}}
						>
							<MenuItem value="">
								<em>اختر فئة</em>
							</MenuItem>
							{loadingMenu ? (
								<MenuItem disabled>
									<CircularProgress size={20} />
								</MenuItem>
							) : (
								categories.map((category) => (
									<MenuItem key={category.id} value={category.id}>
										{category.name}
									</MenuItem>
								))
							)}
						</TextField>
					</Grid>

					<Grid size={12}>
						<Autocomplete
							fullWidth
							sx={{ width: "100%" }}
							options={filteredProducts}
							getOptionLabel={(option) =>
								`${option.name}${
									option.categoryName ? ` (${option.categoryName})` : ""
								} - ${option.price} جنيه`
							}
							value={
								formData.product_id
									? filteredProducts.find(
											(p) => p.id === Number(formData.product_id)
									)
									: null
							}
							onChange={(event, newValue) => {
								if (newValue) {
									handleProductSelect(newValue.id);
								} else {
									setFormData((prev) => ({
										...prev,
										product_id: "",
										price: "",
									}));
								}
							}}
							disabled={loadingMenu || !selectedCategory}
							loading={loadingMenu}
							ListboxProps={{
								sx: {
									direction: "rtl",
									"& .MuiAutocomplete-option": {
										alignItems: "flex-start",
										whiteSpace: "nowrap",
									},
								},
							}}
							slotProps={{
								paper: {
									sx: {
										minWidth: 420,
										width: "100%",
										maxWidth: "min(90vw, 720px)",
										direction: "rtl",
									},
								},
								popper: {
									sx: {
										minWidth: 420,
										maxWidth: "min(90vw, 720px)",
										"& .MuiAutocomplete-paper": {
											minWidth: 420,
											width: "100%",
											maxWidth: "min(90vw, 720px)",
										},
									},
								},
							}}
							renderOption={(props, option) => (
								<Box
									component="li"
									sx={{ display: "flex", alignItems: "center", gap: 1, p: 1 }}
									{...props}
								>
									<Avatar
										src={option.image ? getImageUrl(option.image) : ""}
										alt={option.name}
										sx={{ width: 40, height: 40 }}
									/>
									<Box sx={{ flex: 1, minWidth: 0, pr: 0.5 }}>
										<Typography
											variant="body2"
											sx={{ fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
										>
											{option.name}
										</Typography>
										<Typography
											variant="caption"
											color="textSecondary"
											sx={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
										>
											{option.categoryName && `${option.categoryName} - `}
											{option.price} جنيه
										</Typography>
									</Box>
								</Box>
							)}
							renderInput={(params) => (
								<TextField
									{...params}
									label="اختر المنتج او ابحث عنه"
									sx={{ width: "100%" }}
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{loadingMenu ? <CircularProgress color="inherit" size={20} /> : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
							noOptionsText={selectedCategory ? "لا توجد منتجات" : "اختر فئة أولاً"}
							loadingText="جاري التحميل..."
							isOptionEqualToValue={(option, value) => option.id === value.id}
							filterOptions={(options, { inputValue }) => {
								if (!inputValue) return options;
								return options.filter((option) =>
									option.name.toLowerCase().includes(inputValue.toLowerCase()) ||
									option.categoryName?.toLowerCase().includes(inputValue.toLowerCase())
								);
							}}
						/>
					</Grid>
				</Grid>

				{/* الكمية والسعر والحالة */}
				<Grid container spacing={2} sx={{ mt: 1 }}>
					<Grid size={{ xs: 12, sm: 6 }}>
						<TextField
							label="الكمية"
							type="number"
							name="quantity"
							value={formData.quantity}
							onChange={handleFieldChange}
							inputProps={{ min: 1, step: 1 }}
							fullWidth
						/>
					</Grid>

					<Grid size={{ xs: 12, sm: 6 }}>
						<TextField
							label="السعر"
							type="number"
							name="price"
							value={formData.price}
							inputProps={{ min: 0, step: 0.01 }}
							InputProps={{ readOnly: true }}
							helperText="يتم تحديد السعر تلقائياً حسب المنتج"
							fullWidth
						/>
					</Grid>

					<Grid size={12}>
						<TextField
							label="ملاحظة"
							name="note"
							value={formData.note}
							onChange={handleFieldChange}
							fullWidth
						/>
					</Grid>
				</Grid>
			</DialogContent>

			<DialogActions sx={{ p: 2 }}>
				<Button onClick={handleClose} variant="outlined">
					إلغاء
				</Button>
				<Button onClick={handleAddItem} variant="contained">
					إضافة
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default AddItemDialog;
